// Van Life — state, action, and card types. See RULES.md for the rulebook.

export type Suit = 'Green' | 'Blue' | 'Tan';
export type CardSuit = Suit | 'Wild';
export type Rank = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

export interface Card {
  suit: CardSuit;
  rank: Rank;
  id: string; // unique within a game
}

export type ZoneCardId =
  // Hard (1-2 valid positions)
  | 'summit-exact'   // tracker ends at 4
  | 'momentum'       // tracker ends ≤2 or ≥6
  // Medium (3 positions)
  | 'tundra-winds'   // tracker ends 2-4
  | 'coastal-drift'  // tracker ends 4-6
  | 'ridge-line'     // tracker ends 3-5
  // Medium (4 positions)
  | 'river-delta'    // tracker ends 2-5
  | 'safe-passage'   // tracker never hits 1 or 7
  | 'divergence'     // tracker ends 1-3 or 5-7
  // Easy (5 positions)
  | 'wilderness'     // tracker ends 2-6
  | 'prairie-wind'   // tracker ends 1-5
  | 'sea-breeze'     // tracker ends 3-7
  // Very easy (6 positions)
  | 'open-country';  // tracker ends 2-7

export type ChallengeCardId =
  // Hard
  | 'bookends'       // win tricks 1 and 8
  | 'alternating'    // never win consecutive tricks
  | 'odd-tricks'     // win only on odd trick numbers
  | 'suit-sampler'   // win one of each suit
  | 'streak'         // win 3 consecutive tricks
  | 'clean-sweep'    // win where your card is only of its suit
  // Medium
  | 'low-road'       // win with rank ≤2
  | 'iron-patience'  // don't win first 3 tricks
  | 'strong-finish'  // more wins in 2nd half
  | 'deck-beater'    // more wins than park deck
  | 'double-run'     // win 2+ consecutive tricks
  | 'pack-light'     // win 2-5 tricks total
  // Easy
  | 'first-blood'    // win trick 1
  | 'last-stand'     // win trick 8
  | 'first-or-last'  // win trick 1 OR the last trick
  | 'low-gear'       // win with rank ≤4
  // Very easy
  | 'early-bird'     // win ≥1 trick in tricks 1-4
  | 'night-owl'      // win ≥1 trick in tricks 5-8
  | 'pioneer-spirit';// win ≥1 trick

export interface ZoneCard {
  id: ZoneCardId;
  name: string;
  description: string;
}

export interface ChallengeCard {
  id: ChallengeCardId;
  name: string;
  description: string;
}

export interface CompletedTrick {
  trickNumber: number;
  leadPlayerId: string;
  cards: Record<string, Card>; // keyed by playerId or 'deck'
  winnerId: string | 'deck';
  winnerCard: Card;
}

export interface MatchResult {
  trackerFinal: number;
  deckTrickCount: number;
  parkResults: Array<{
    parkId: string;
    playerId: string;
    zoneMet: boolean;
    challengeMet: boolean;
    conquered: boolean;
  }>;
}

export interface VanLifeMatchState {
  matchNumber: number;
  totalTricks: number;
  hands: Record<string, Card[]>;
  parkDeck: Card[];
  currentDeckCard: Card | null; // null = park deck exhausted; players-only tricks
  deckLeads: boolean;        // true = deck card sets the led suit; false = lead player does
  trickNumber: number;       // 1-based, current trick
  leadPlayerId: string;
  leadCard: Card | null;
  followCard: Card | null;
  subPhase: 'discard' | 'lead' | 'follow';
  pendingDiscards: Record<string, boolean>; // playerId → has discarded
  completedTricks: CompletedTrick[];
  tracker: number;           // 1-7, starts at 4
  trackerHistory: number[];  // for safe-passage zone check
  deckTrickCount: number;
  result: MatchResult | null;
}

export interface VanLifePlayer {
  id: string;
  name: string;
  playerNumber: 1 | 2;
  color: string;
  currentParkId: string;
}

export interface VanLifePark {
  id: string;
  name: string;
  zoneCardId: ZoneCardId;
  challengeCardId: ChallengeCardId;
  conquered: boolean;
}

export interface VanLifeConfig {
  botStrength?: 'weak' | 'medium' | 'strong';
  deckDrawsToSuit?: boolean; // when following, deck draws cards until it finds a valid suit
}
export const DEFAULT_CONFIG: VanLifeConfig = { botStrength: 'medium', deckDrawsToSuit: false };

export interface VanLifeState {
  config: VanLifeConfig;
  phase: 'trick-taking' | 'match-result' | 'movement' | 'finished';
  matchNumber: number;       // 1-5
  players: VanLifePlayer[];
  parks: VanLifePark[];
  wildPool: Card[];          // wilds shuffled into every deal; grows from wildDeck each match
  wildDeck: Card[];          // undealt wilds (unique ranks 1-9); one moves to pool after each match
  pendingMoves: Record<string, string>; // playerId → chosen park id
  match: VanLifeMatchState | null;
  gameWon: boolean;
  rng: number;
}

export type VanLifeAction =
  | { type: 'chooseMove';   playerId: string; targetParkId: string }
  | { type: 'discardCards'; playerId: string; cardIds: string[] }
  | { type: 'playCard';     playerId: string; cardId: string }
  | { type: 'advanceMatch'; playerId: string };
